import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PhotoUploader from "@/components/site/PhotoUploader";
import { Button } from "@/components/ui/button";
import { Pin, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline photo manager for a single school (used inside admin edit dialog).
 * - Drag-and-drop upload (direct to /api/schools/{id}/photos)
 * - Grid of existing photos with Pin & Delete controls
 */
export default function SchoolPhotosManager({ schoolId }) {
  const [photos, setPhotos] = useState([]);
  const [pending, setPending] = useState([]);
  const [pendingCaptions, setPendingCaptions] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/schools/${schoolId}/photos`).then((r) => setPhotos(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (schoolId) load(); }, [schoolId]);

  const doUpload = async () => {
    if (!pending.length) return;
    setBusy(true);
    try {
      for (let i = 0; i < pending.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/schools/${schoolId}/photos`, { data_url: pending[i], caption: (pendingCaptions[i] || "").slice(0, 200) });
      }
      toast.success(`${pending.length} foto diunggah`);
      setPending([]);
      setPendingCaptions([]);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengunggah"); }
    finally { setBusy(false); }
  };

  const togglePin = async (p) => {
    try {
      await api.patch(`/admin/photos/${p.photo_id}/pin`, { pinned: !p.pinned });
      toast.success(p.pinned ? "Pin dilepas" : "Foto dipinkan ke atas");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const removePhoto = async (p) => {
    if (!window.confirm("Hapus foto ini?")) return;
    try {
      await api.delete(`/admin/photos/${p.photo_id}`);
      toast.success("Foto dihapus");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="border-t border-[#EAE7E0] pt-4 mt-2" data-testid="school-photos-manager">
      <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-2">Galeri foto sekolah</div>
      <PhotoUploader value={pending} onChange={setPending} captions={pendingCaptions} onCaptionsChange={setPendingCaptions} showCaptions maxPhotos={10} testidPrefix="admin-school-photo" label="Unggah foto ke galeri" />
      {pending.length > 0 && (
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="outline" onClick={() => { setPending([]); setPendingCaptions([]); }} className="rounded-full h-9">Bersihkan</Button>
          <Button disabled={busy} onClick={doUpload} data-testid="admin-school-photo-submit" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-9">
            {busy ? "Mengunggah…" : `Unggah ${pending.length} foto`}
          </Button>
        </div>
      )}
      {photos.length > 0 && (
        <div className="mt-4">
          <div className="text-xs text-[#4B5563] mb-2">{photos.length} foto tersimpan · pinned tampil di atas galeri publik</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((p, i) => (
              <div key={p.photo_id} data-testid={`admin-school-photo-tile-${i}`} className={"relative rounded-xl overflow-hidden border aspect-[4/3] bg-[#F3F1ED] " + (p.pinned ? "border-[#9A3412] ring-2 ring-[#FED7AA]" : "border-[#EAE7E0]")}>
                <img src={p.data_url} alt={p.caption || "foto sekolah"} className="w-full h-full object-cover" />
                {p.pinned && <span className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full bg-[#FED7AA] text-[#9A3412] text-[10px] uppercase tracking-wider font-semibold inline-flex items-center gap-1"><Pin size={10} /> Dipin</span>}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 flex items-center justify-between opacity-0 hover:opacity-100 transition-opacity">
                  <div className="text-[10px] text-white truncate max-w-[60%]">{p.uploader_name}</div>
                  <div className="flex gap-1">
                    <button onClick={() => togglePin(p)} data-testid={`admin-school-photo-pin-${i}`} title={p.pinned ? "Lepas pin" : "Pin"} className="w-7 h-7 rounded-full bg-white/90 hover:bg-white text-[#9A3412] flex items-center justify-center"><Pin size={12} /></button>
                    <button onClick={() => removePhoto(p)} data-testid={`admin-school-photo-delete-${i}`} title="Hapus" className="w-7 h-7 rounded-full bg-white/90 hover:bg-white text-[#B91C1C] flex items-center justify-center"><Trash2 size={12} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
